import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { sendTransactionSync } from '@wagmi/core'
import { Rpc } from 'accounts'
import { type Hex } from 'ox'
import { useMemo, useState } from 'react'
import { encodeFunctionData, erc20Abi, isAddress, parseUnits } from 'viem'
import type { tempo } from 'viem/chains'
import { Abis, Addresses } from 'viem/tempo'
import { useConnection, usePrepareTransactionRequest } from 'wagmi'
import { Hooks } from 'wagmi/tempo'
import * as z from 'zod/mini'

import { useAssets } from '#/lib/assets.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { wagmiConfig } from '#/lib/config.js'
import { useDebounce } from '#/lib/react.js'
import { formatQuantity, isTip20 } from '#/lib/token.js'
import { zoneId as defaultZoneId } from '#/lib/zone.js'
import * as ZoneFrames from '../-frames/zones/index.js'
import { usePrepareAlert } from '../-hooks/usePrepareAlert.js'
import { useRequireFunds } from '../-hooks/useRequireFunds.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'
import * as SameSite from '../-lib/SameSite.js'

const decimalRegex = /^\d*\.?\d*$/

export const Route = createFileRoute('/remote/rpc/wallet_depositZone')({
  component: Component,
  validateSearch: (search) => RemoteRpc.validateSearch(search, { method: 'wallet_depositZone' }),
})

function Component() {
  const request = Route.useSearch()
  const rpc = RemoteRpc.use()
  const { address, chainId } = useConnection()

  const params = request._decoded?.params?.[0]
  const parentChainId = getSupportedChainId(chainId)
  const zoneId = params?.zoneId ?? defaultZoneId
  // Cross-site apps don't get the wallet's relay-funded sponsorship.
  // App-supplied fee payer addresses/URLs (strings) are still respected.
  const sameSite = SameSite.useValidate(rpc.consumer.origin)
  const feePayer = sameSite || typeof params?.feePayer === 'string' ? params?.feePayer : false

  const [values, setValues] = useState(() => ({
    amount: params?.amount ?? '',
    token: params?.token ?? '',
  }))

  const assets = useAssets({ account: address, chainId: parentChainId })

  const sendableAssets = useMemo(() => {
    if (!assets.data) return undefined
    return assets.data
      .filter((a) => isTip20(a.address))
      .slice()
      .sort((a, b) => {
        const aBal = BigInt(a.balance)
        const bBal = BigInt(b.balance)
        if (aBal === bBal) return a.symbol.localeCompare(b.symbol)
        return bBal > aBal ? 1 : -1
      })
  }, [assets.data])

  const effectiveToken = useMemo(() => {
    if (values.token) return values.token
    if (!sendableAssets || sendableAssets.length === 0) return ''
    const preferred =
      params?.token &&
      sendableAssets.find((a) => a.address.toLowerCase() === params.token!.toLowerCase())
    return (preferred ?? sendableAssets[0]).address
  }, [params?.token, sendableAssets, values.token])

  const selectedAsset = useMemo(() => {
    if (!effectiveToken || !sendableAssets) return undefined
    return sendableAssets.find((a) => a.address.toLowerCase() === effectiveToken.toLowerCase())
  }, [sendableAssets, effectiveToken])

  const tokenError = useMemo(() => {
    if (!effectiveToken) return undefined
    if (!isAddress(effectiveToken)) return 'Invalid asset.'
    if (!isTip20(effectiveToken)) return 'Only TIP-20 assets can be deposited into a zone.'
    if (!selectedAsset && !assets.isLoading) return 'You don’t hold this asset.'
    return undefined
  }, [assets.isLoading, effectiveToken, selectedAsset])

  const amount = useMemo<bigint | undefined>(() => {
    if (!values.amount || !selectedAsset || values.amount === '.') return undefined
    const normalized = values.amount.startsWith('.')
      ? `0${values.amount}`
      : values.amount.endsWith('.')
        ? values.amount.slice(0, -1)
        : values.amount
    const decimals = normalized.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return undefined
    try {
      return parseUnits(normalized, selectedAsset.decimals)
    } catch {
      return undefined
    }
  }, [selectedAsset, values.amount])

  const amountError = useMemo(() => {
    if (!values.amount) return undefined
    if (!decimalRegex.test(values.amount) || values.amount === '.') return 'Enter a valid amount.'
    if (!selectedAsset) return undefined
    const decimals = values.amount.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return `Use at most ${selectedAsset.decimals} decimals.`
    if (amount === undefined) return 'Enter a valid amount.'
    if (amount <= 0n) return 'Enter an amount greater than 0.'
    // Insufficient-balance is surfaced via `useRequireFunds` (relay-side
    // `requireFunds` capability), so the user can fund/onramp inline.
    return undefined
  }, [amount, selectedAsset, values.amount])

  const portalAddress = Addresses.zonePortal(zoneId)
  const allowance = Hooks.token.useGetAllowance({
    chainId: parentChainId,
    query: {
      enabled: !!address && !!selectedAsset,
      refetchOnWindowFocus: false,
    },
    spender: portalAddress,
    ...(address ? { account: address } : {}),
    ...(selectedAsset ? { token: selectedAsset.address as Hex.Hex } : {}),
  })

  // Build the call batch: optional `approve` + `deposit` on the zone portal.
  const calls = useMemo(() => {
    if (!address || !selectedAsset || amount === undefined || amount <= 0n) return undefined
    const out: { to: Hex.Hex; data: Hex.Hex; value?: bigint }[] = []
    const currentAllowance = allowance.data?.amount ?? 0n
    if (currentAllowance < amount)
      out.push({
        to: selectedAsset.address as Hex.Hex,
        data: encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [portalAddress, amount],
        }),
      })
    out.push({
      to: portalAddress,
      data: encodeFunctionData({
        abi: Abis.zonePortal,
        functionName: 'deposit',
        args: [
          selectedAsset.address as Hex.Hex,
          address,
          amount,
          '0x0000000000000000000000000000000000000000000000000000000000000000',
          address,
        ],
      }),
    })
    return out
  }, [address, allowance.data, amount, portalAddress, selectedAsset])

  // Debounce so we don't issue a relay prepare on every keystroke.
  const debouncedCalls = useDebounce(calls, 300)

  const prepare = usePrepareTransactionRequest({
    calls: debouncedCalls,
    capabilities: { errors: true },
    to: debouncedCalls?.[0]?.to,
    chainId: parentChainId as typeof tempo.id,
    feePayer: feePayer as never,
    type: 'tempo',
    query: {
      enabled: !!address && !!debouncedCalls,
      refetchOnWindowFocus: false,
      // Poll while waiting for funds to arrive (deposit/onramp) so the
      // requireFunds capability clears as soon as a deposit confirms.
      refetchInterval: (query) => (query.state.data?._capabilities?.requireFunds ? 2_000 : false),
      retry: false,
      staleTime: 0,
      gcTime: 0,
    },
  })

  const prepareData = prepare.data
  const capabilities = prepare.data?._capabilities
  const preparingCurrentInput = calls !== debouncedCalls || prepare.isFetching
  const autoSwap = capabilities?.autoSwap

  const funds = useRequireFunds({
    address,
    chainId: parentChainId,
    prepareQuery: prepare,
  })
  const requireFunds = funds.requireFunds

  const confirm = useMutation({
    mutationFn: async () => {
      if (preparingCurrentInput) throw new Error('Transaction not prepared for current inputs.')
      if (!prepareData) throw new Error('Transaction not prepared.')
      // If the relay quote includes autoSwap calls, prepend them so the
      // missing funds are sourced from the user's other balances atomically.
      const swapCalls = autoSwap?.calls
      const data = swapCalls
        ? { ...prepareData, calls: [...swapCalls, ...(prepareData.calls ?? [])] }
        : prepareData
      const receipt = await sendTransactionSync(wagmiConfig, data as never)
      await rpc.resolve({ receipt: z.encode(Rpc.receipt as never, receipt as never) })
      return receipt
    },
  })

  const host = RemoteRpc.useExternalHost()

  const fieldErrors = !!(amountError || tokenError)
  const alert = usePrepareAlert({
    mutation: { error: confirm.error },
    query: prepare,
    suppress: fieldErrors,
  })

  const fee = capabilities?.fee
  const { sponsor, sponsored: sponsoredCap } = capabilities ?? {}
  const sponsored = !!sponsor || !!sponsoredCap
  const inputsValid = !!selectedAsset && amount !== undefined && amount > 0n && !fieldErrors
  const feePending = inputsValid && (allowance.isLoading || prepare.isFetching)

  const ready =
    inputsValid &&
    !!prepareData &&
    !preparingCurrentInput &&
    !requireFunds &&
    !confirm.isPending &&
    !capabilities?.error

  const primaryLabel = confirm.isError ? 'Try again' : confirm.isPending ? 'Depositing…' : 'Deposit'

  // Asset rows for the dropdown — narrow shape keeps the frame presentational.
  const assetOptions = useMemo(() => {
    if (!sendableAssets) return undefined
    return sendableAssets.map((a) => ({
      address: a.address,
      balance: formatQuantity(BigInt(a.balance), a.decimals),
      symbol: a.symbol,
    }))
  }, [sendableAssets])

  if (funds.frame) return funds.frame

  return (
    <ZoneFrames.Deposit
      actions={funds.actions}
      alert={alert}
      assets={assetOptions}
      assetsLoading={assets.isLoading}
      autoSwap={autoSwap}
      chainId={parentChainId}
      confirming={confirm.isPending}
      errors={{ amount: amountError, token: tokenError }}
      fee={fee}
      feePending={feePending && !fee}
      host={host}
      loading={!sendableAssets}
      onChange={(event) => {
        confirm.reset()
        if (event.field === 'amount') {
          const next = event.value.replaceAll(',', '.')
          if (!decimalRegex.test(next)) return
          setValues((current) => ({ ...current, amount: next }))
          return
        }
        setValues((current) => ({ ...current, token: event.value }))
      }}
      onConfirm={() => confirm.mutate()}
      onMax={
        selectedAsset
          ? () => {
              confirm.reset()
              setValues((current) => ({
                ...current,
                amount: formatQuantity(
                  BigInt(selectedAsset.balance),
                  selectedAsset.decimals,
                ).replaceAll(',', ''),
              }))
            }
          : undefined
      }
      onReject={() => rpc.reject()}
      primaryLabel={primaryLabel}
      ready={ready}
      selectedAsset={
        selectedAsset
          ? {
              address: selectedAsset.address,
              balance: formatQuantity(BigInt(selectedAsset.balance), selectedAsset.decimals),
              symbol: selectedAsset.symbol,
            }
          : undefined
      }
      sponsored={sponsored}
      values={{ ...values, token: effectiveToken }}
    />
  )
}
